import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { galleryAPI } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { ArrowLeft, Image, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { SEO } from '../seo/SEO';

export const GalleryPage = ({ siteKey }) => {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    const loadAlbums = async () => {
      try {
        const response = await galleryAPI.getAlbums(siteKey);
        setAlbums(response.data);
      } catch (error) {
        console.error('Error loading albums:', error);
      } finally {
        setLoading(false);
      }
    };
    loadAlbums();
  }, [siteKey]);

  const openLightbox = (album, index) => {
    setSelectedAlbum(album);
    setLightboxIndex(index);
  };

  const closeLightbox = () => {
    setSelectedAlbum(null);
    setLightboxIndex(null);
  };

  const nextImage = () => {
    if (selectedAlbum && lightboxIndex < selectedAlbum.images.length - 1) {
      setLightboxIndex(lightboxIndex + 1);
    }
  };

  const prevImage = () => {
    if (lightboxIndex > 0) {
      setLightboxIndex(lightboxIndex - 1);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="py-12">
      <SEO
        siteKey={siteKey}
        title="Fotogalerie"
        description={`Fotogalerie ze života ${siteKey === 'ms' ? 'MŠ Cestička' : 'dětské skupiny Chaloupka na vršku'}. Prohlédněte si, jak trávíme čas, akce a výlety s dětmi.`}
        keywords={['fotogalerie', 'fotky', 'alba', 'akce', 'výlety']}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <Link to={`/${siteKey}`} className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#45AAF2] mb-4" data-testid="back-link">
            <ArrowLeft size={18} />
            Zpet na uvodní stránku
          </Link>
          <h1 className="text-4xl font-bold text-[#2D3748]" data-testid="page-title">Fotogalerie</h1>
          <p className="text-[#64748B] mt-2">Nahlédnete do života naší školky</p>
        </div>

        {albums.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {albums.map((album, index) => (
              <div 
                key={album.id} 
                className="bg-white rounded-2xl overflow-hidden border-3 border-[#F0F4F8] shadow-sm hover:shadow-lg transition-all animate-fade-in-up"
                style={{ animationDelay: `${index * 50}ms` }}
                data-testid={`album-${index}`}
              >
                <div className="relative aspect-video cursor-pointer" onClick={() => openLightbox(album, 0)}>
                  <img 
                    src={album.cover_image || album.images[0]?.url || 'https://via.placeholder.com/400x300'} 
                    alt={album.title} 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                    <div className="text-white flex items-center gap-2">
                      <Image size={24} />
                      <span className="font-medium">{album.images?.length || 0} fotek</span>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-lg text-[#2D3748] mb-1">{album.title}</h3>
                  {album.description && (
                    <p className="text-sm text-[#64748B] line-clamp-2">{album.description}</p>
                  )}
                  <div className="mt-2 text-xs text-[#64748B]">{formatDate(album.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <Image size={48} className="mx-auto text-[#CBD5E1] mb-4" />
            <div className="text-[#64748B]">Zatím nejsou žádné fotogalerie</div>
          </div>
        )}
      </div>

      {selectedAlbum && lightboxIndex !== null && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" data-testid="lightbox">
          <button onClick={closeLightbox} className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-full">
            <X size={32} />
          </button>
          {lightboxIndex > 0 && (
            <button onClick={prevImage} className="absolute left-4 text-white p-2 hover:bg-white/10 rounded-full">
              <ChevronLeft size={32} />
            </button>
          )}
          {lightboxIndex < selectedAlbum.images.length - 1 && (
            <button onClick={nextImage} className="absolute right-4 text-white p-2 hover:bg-white/10 rounded-full">
              <ChevronRight size={32} />
            </button>
          )}
          <div className="max-w-4xl max-h-[80vh] px-16">
            <img 
              src={selectedAlbum.images[lightboxIndex]?.url} 
              alt={selectedAlbum.images[lightboxIndex]?.caption || ''} 
              className="max-w-full max-h-[80vh] object-contain"
            />
            {selectedAlbum.images[lightboxIndex]?.caption && (
              <p className="text-white text-center mt-4">{selectedAlbum.images[lightboxIndex].caption}</p>
            )}
          </div>
          <div className="absolute bottom-4 text-white text-sm">
            {lightboxIndex + 1} / {selectedAlbum.images.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
